export type ShareOrCopyUrlArgs = {
  title: string;
  text?: string;
  url: string;
};

export type ShareOrCopyUrlResult = 'shared' | 'copied';

export function getBrowserOrigin(): string {
  return window.location.origin.replace(/\/+$/, '');
}

export async function shareOrCopyUrl({
  title,
  text,
  url,
}: ShareOrCopyUrlArgs): Promise<ShareOrCopyUrlResult> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      return 'shared';
    } catch {
      // Fall back to copying so desktop browsers keep the same share affordance.
    }
  }

  await navigator.clipboard.writeText(url);
  return 'copied';
}

export type DownloadBinaryAssetArgs = {
  url: string;
  fileName?: string;
};

function resolveDownloadFileName(
  requestedFileName: string | undefined,
  dispositionHeader: string | null,
): string {
  if (requestedFileName) {
    return requestedFileName;
  }

  const match = dispositionHeader?.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? 'optimistic-news-card.png';
}

async function fetchAsFile(
  url: string,
  fileName: string | undefined,
): Promise<File> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const resolvedName = resolveDownloadFileName(
    fileName,
    response.headers.get('content-disposition'),
  );
  return new File([blob], resolvedName, { type: blob.type || 'image/png' });
}

function triggerBrowserDownload(file: File): void {
  const objectUrl = window.URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = file.name;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Delay revocation so the browser has time to start reading the blob.
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 3000);
}

export type ShareOrDownloadCardResult = 'shared' | 'downloaded';

export async function shareOrDownloadCard({
  url,
  fileName,
  title,
  text,
}: DownloadBinaryAssetArgs & {
  title?: string;
  text?: string;
}): Promise<ShareOrDownloadCardResult> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const resolvedName = resolveDownloadFileName(
    fileName,
    response.headers.get('content-disposition'),
  );
  const file = new File([blob], resolvedName, {
    type: blob.type || 'image/png',
  });

  if (
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({
        ...(title ? { title } : {}),
        ...(text ? { text } : {}),
        files: [file],
      });
      return 'shared';
    } catch {
      // User cancelled or share failed — fall through to download.
    }
  }

  triggerBrowserDownload(file);
  return 'downloaded';
}

export async function downloadBinaryAsset({
  url,
  fileName,
}: DownloadBinaryAssetArgs): Promise<void> {
  const file = await fetchAsFile(url, fileName);
  triggerBrowserDownload(file);
}

export function triggerJsonDownload(data: unknown, fileName: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 3000);
}
